import { Link } from '@tanstack/react-router'
import { ChevronRightIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { CariOnly } from '@app/components/entity-link'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardHeader, CardTitle } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import { formatDate, formatQty } from './format'
import { AmountStack } from './ui'
import type { PurchaseSummaryResponse, PurchaseSupplierExposure } from './api'

/**
 * Tedarikçi panosu — the purchasing board strip.
 *
 * The question this answers is the one Korgun cannot: "who still owes us
 * goods, and how much?". Every supplier with an OPEN purchase order that still
 * has a remaining quantity gets a row; clicking it filters the order list below
 * to that supplier, and the icon at the end opens their cari card — for the
 * accounts that have cari at all (see components/entity-link.tsx).
 *
 * Suppliers are ranked by remaining QUANTITY, not by money: only 115 of the 563
 * purchase orders carry a price at all, so an amount ranking would sort mostly
 * zeros. The open value is still shown per currency where it exists — and it is
 * never summed across currencies, because Korgun never converts.
 */

/** Rows visible before the "show all" toggle. */
const COLLAPSED_COUNT = 6

/**
 * How long an open order has to have been standing before the date starts
 * carrying a colour. Lead times here are weeks, not months: past ~2 months
 * somebody should have chased it, past ~6 it is very likely dead paperwork
 * nobody ever closed (97 such orders exist in the book).
 */
const STALE_DAYS = 60
const VERY_STALE_DAYS = 180

/**
 * Whole days between an API date ('YYYY-MM-DD', Turkish wall-clock) and today.
 *
 * Both sides are collapsed to a calendar day before subtracting, so this never
 * turns into an off-by-one at midnight the way `Date.parse` on a bare date
 * (which is read as UTC) does.
 */
function daysSince(iso: string): number | null {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return null
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((today - Date.UTC(year, month - 1, day)) / 86_400_000)
}

/** "3 ay" / "2 yıl" — coarse on purpose; the exact date sits right next to it. */
function ageLabel(days: number): string | null {
  if (days < 30) return null
  if (days < 365) return `${Math.floor(days / 30)} ay`
  const years = Math.floor(days / 365)
  return `${years} yıl`
}

function staleToneClass(days: number | null): string {
  if (days === null) return 'text-ink-muted'
  if (days >= VERY_STALE_DAYS) return 'text-kordovan'
  if (days >= STALE_DAYS) return 'text-hardal'
  return 'text-ink-muted'
}

export function SupplierBoard({
  data,
  isPending,
  activeSupplier,
  onSelect,
}: {
  data: PurchaseSummaryResponse | undefined
  isPending: boolean
  /** Cari code currently filtering the list, if the filter came from this board. */
  activeSupplier: string | null
  onSelect: (cariKod: string) => void
}) {
  const [expanded, setExpanded] = useState(false)

  if (isPending) {
    return <BoardSkeleton />
  }

  // A failed board must not take the order list down with it — the list is the
  // screen's real payload, the board is context.
  if (!data || data.suppliers.length === 0) {
    return null
  }

  const { suppliers, supplierCount, totals } = data
  const shown = expanded ? suppliers : suppliers.slice(0, COLLAPSED_COUNT)
  const hidden = supplierCount - shown.length

  return (
    <Card>
      <CardHeader className="flex-wrap gap-y-1">
        <div>
          <CardTitle>Tedarikçi panosu</CardTitle>
          <p className="mt-0.5 text-micro text-ink-muted">
            Bir tedarikçiye dokunun: alttaki sipariş listesi o tedarikçinin açık
            siparişlerine daralır.
          </p>
        </div>
        <span className="font-mono text-micro tnum text-ink-muted">
          {formatQty(totals.openOrders)} açık sipariş · {formatQty(totals.kalanLines)} satır ·{' '}
          <span className="font-semibold text-ink">{formatQty(totals.kalanQty)}</span> kalan
          {totals.overdueOrders > 0 ? (
            <span className="text-hardal"> · {formatQty(totals.overdueOrders)} geciken</span>
          ) : null}
        </span>
      </CardHeader>

      <div className="grid gap-1.5 px-3.5 py-3 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((supplier) => (
          <SupplierRow
            key={supplier.cariKod}
            supplier={supplier}
            active={activeSupplier === supplier.cariKod}
            onSelect={() => onSelect(supplier.cariKod)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
        <span className="text-micro text-ink-muted">
          Açık yükü olan {formatQty(supplierCount)} tedarikçi · açık tutar{' '}
          {totals.amounts.length === 0 ? (
            'girilmemiş'
          ) : (
            <span className="font-mono tnum text-ink">
              {totals.amounts.map((a) => `${formatQty(a.amount)} ${a.currency}`).join(' · ')}
            </span>
          )}
        </span>
        {hidden > 0 || expanded ? (
          <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Daha az göster' : `Tümünü göster (${formatQty(supplierCount)})`}
          </Button>
        ) : null}
      </div>
    </Card>
  )
}

/**
 * One tile.
 *
 * Everything a tile knows is written on it. The date used to be a bare
 * "08.05.2026" with the explanation in a `title`, and the overdue count a bare
 * "2" in an amber pill — both invisible on a touch screen, and both easy to
 * misread as something else (a delivery date, a line count). They now carry
 * their own labels: "en eski 08.05.2026 · 3 ay" and "geciken 2".
 */
function SupplierRow({
  supplier,
  active,
  onSelect,
}: {
  supplier: PurchaseSupplierExposure
  active: boolean
  onSelect: () => void
}) {
  const standingDays = supplier.oldestOrderDate ? daysSince(supplier.oldestOrderDate) : null
  const age = standingDays === null ? null : ageLabel(standingDays)

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xs border px-2 py-1.5 transition-colors',
        active
          ? 'border-kordovan/30 bg-kordovan/6'
          : 'border-hairline bg-kagit hover:border-ink-muted/40',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={active}
        className="group min-w-0 flex-1 text-left"
      >
        <span className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              'min-w-0 truncate text-data font-medium',
              active ? 'text-kordovan' : 'text-ink',
            )}
          >
            {supplier.name}
          </span>
          {/* The tiles are filters, not decoration — the chevron is the only
              thing that says so before you click one. */}
          <ChevronRightIcon
            aria-hidden
            className={cn(
              'size-3 shrink-0 transition-colors',
              active ? 'text-kordovan' : 'text-ink-muted/45 group-hover:text-ink-muted',
            )}
          />
        </span>
        <span className="mt-px flex flex-wrap items-center gap-x-1.5 font-mono text-micro tnum text-ink-muted">
          <span>{formatQty(supplier.openOrders)} sipariş</span>
          <span aria-hidden>·</span>
          <span>{formatQty(supplier.kalanLines)} satır</span>
        </span>
        {supplier.oldestOrderDate ? (
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className={cn('font-mono text-micro tnum', staleToneClass(standingDays))}>
              en eski {formatDate(supplier.oldestOrderDate)}
              {age ? ` · ${age}` : ''}
            </span>
            {supplier.overdueOrders > 0 ? (
              <Badge tone="warn">geciken {formatQty(supplier.overdueOrders)}</Badge>
            ) : null}
          </span>
        ) : supplier.overdueOrders > 0 ? (
          <span className="mt-0.5 block">
            <Badge tone="warn">geciken {formatQty(supplier.overdueOrders)}</Badge>
          </span>
        ) : null}
      </button>

      <span className="flex shrink-0 flex-col items-end leading-tight">
        <span className="font-mono text-data tnum font-semibold text-ink">
          {formatQty(supplier.kalanQty)}
        </span>
        <AmountStack amounts={supplier.amounts} className="text-micro text-ink-muted" />
      </span>

      {/* An icon is nothing but its destination, so an account without cari
          gets no icon rather than a dead one — the tile keeps its filter. */}
      <CariOnly>
        <Link
          to="/cari/$ckod"
          params={{ ckod: supplier.cariKod }}
          title={`${supplier.cariKod} cari kartını aç`}
          aria-label={`${supplier.name} cari kartını aç`}
          className="grid size-5 shrink-0 place-items-center rounded-xs text-ink-muted transition-colors hover:bg-kordovan/8 hover:text-kordovan"
        >
          <ExternalLinkIcon className="size-3.5" aria-hidden />
        </Link>
      </CariOnly>
    </div>
  )
}

function BoardSkeleton() {
  return (
    <Card aria-hidden>
      <CardHeader>
        <span className="block h-3 w-32 animate-pulse rounded-xs bg-hairline" />
      </CardHeader>
      <div className="grid gap-1.5 px-3.5 py-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rounded-xs border border-hairline bg-kagit px-2 py-1.5">
            <span className="block h-2.5 w-28 animate-pulse rounded-xs bg-hairline" />
            <span className="mt-1.5 block h-2 w-20 animate-pulse rounded-xs bg-hairline" />
          </div>
        ))}
      </div>
    </Card>
  )
}
